import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { type Firestore, connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import type { TakiConfig } from '@taki/runtime-config';

/**
 * Instances Firestore déjà branchées sur l'émulateur.
 *
 * `connectFirestoreEmulator` ne tolère pas d'être appelé deux fois sur la même
 * instance : comme `initFirebase` RÉUTILISE une app existante (donc la même
 * instance Firestore), on mémorise ce qui est déjà branché.
 */
const emulatorBoundStores = new WeakSet<Firestore>();

/**
 * Initialise (ou réutilise) une application Firebase à partir de la config
 * chargée au runtime. On nomme l'app d'après l'orgId pour permettre plusieurs
 * instances isolées dans un même process sans collision.
 *
 * Si la config runtime porte un bloc `emulators` (dev 100% local, cf.
 * `pnpm dev:local`), Firestore est branché sur l'émulateur au lieu du cloud.
 * C'est bien la CONFIG qui pilote, jamais une variable d'env figée au build.
 */
export function initFirebase(cfg: TakiConfig): { app: FirebaseApp; db: Firestore } {
  const appName = `taki-${cfg.orgId}`;
  const existing = getApps().find((a) => a.name === appName);
  const app = existing ?? initializeApp(cfg.firebaseConfig, appName);
  const db = getFirestore(app);

  const firestoreEmulator = cfg.emulators?.firestore;
  if (firestoreEmulator && !emulatorBoundStores.has(db)) {
    connectFirestoreEmulator(db, firestoreEmulator.host, firestoreEmulator.port);
    emulatorBoundStores.add(db);
    console.info(
      `[@taki/sdk] Firestore branché sur l'émulateur ${firestoreEmulator.host}:${firestoreEmulator.port} (données locales jetables).`,
    );
  }

  return { app, db };
}
